// Distances and speeds in the viewer's units: their choice, else their locale's.

/** Distances in km and km/h, or miles and mph. */
export type Units = "metric" | "imperial";

/** What the viewer picked on the account page: their region's units, or one of them. */
export type UnitsChoice = "auto" | Units;

export const UNITS_CHOICES: readonly UnitsChoice[] = ["auto", "metric", "imperial"];

const STORAGE_KEY = "tentwenty.units";
/** The service worker can't read localStorage; it reads the choice from this cache entry. */
const SW_CACHE = "tentwenty-prefs";
const SW_CACHE_PATH = "/__prefs/units";

/** Regions that give road distances and speeds in miles. */
const MILES_REGIONS = new Set(["US", "GB", "LR", "MM"]);

const METRES_PER_MILE = 1609.344;
const FEET_PER_METRE = 3.28084;

/** The region of `locale` (default: the browser's), if it names or implies one. */
export function region(locale?: string): string | undefined {
  try {
    return new Intl.Locale(locale ?? Intl.DateTimeFormat().resolvedOptions().locale).maximize().region;
  } catch {
    return undefined;
  }
}

/** Miles for the US, UK, Liberia and Myanmar; otherwise kilometres. */
export function unitsFor(locale?: string): Units {
  const r = region(locale);
  return r !== undefined && MILES_REGIONS.has(r) ? "imperial" : "metric";
}

export function parseUnitsChoice(value: string | null | undefined): UnitsChoice {
  return UNITS_CHOICES.find((choice) => choice === value) ?? "auto";
}

/** The units to show: the chosen ones, or the locale's for "auto". */
export function resolveUnits(choice: UnitsChoice, locale?: string): Units {
  return choice === "auto" ? unitsFor(locale) : choice;
}

function readStoredChoice(): UnitsChoice {
  try {
    return parseUnitsChoice(localStorage.getItem(STORAGE_KEY));
  } catch {
    return "auto";
  }
}

// The choice for this page: localStorage's, or the last one made here if it couldn't be stored.
let current: UnitsChoice | undefined;

export function storedUnitsChoice(): UnitsChoice {
  current ??= readStoredChoice();
  return current;
}

const listeners = new Set<() => void>();

/** Remembers the choice in this browser and hands it to the service worker for alert text. */
export function storeUnitsChoice(choice: UnitsChoice): void {
  current = choice;
  try {
    if (choice === "auto") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Private browsing: the choice lasts for this page only.
  }
  for (const listener of listeners) listener();
  shareUnitsWithServiceWorker();
}

let mirroring = Promise.resolve();

/**
 * Copies the current choice into Cache Storage for the service worker. Writes run one at
 * a time and each copies the choice as it is then, so the last one always wins. Also run
 * at startup, to restore a cleared or failed copy.
 */
export function shareUnitsWithServiceWorker(): void {
  mirroring = mirroring.then(async () => {
    try {
      const cache = await caches.open(SW_CACHE);
      await cache.put(SW_CACHE_PATH, new Response(storedUnitsChoice()));
    } catch {
      // No Cache Storage (insecure origin, private browsing): alerts use the browser's region.
    }
  });
}

// Another tab's change: take it up, and copy it to the service worker again in case a write
// queued here before it lands after the other tab's.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    current = readStoredChoice();
    for (const listener of listeners) listener();
    shareUnitsWithServiceWorker();
  });
}

/** Calls `listener` when the choice changes here or in another tab. */
export function subscribeUnitsChoice(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function distanceText(metres: number, units: Units = "metric"): string {
  if (units === "metric") {
    if (metres < 1000) return `${Math.round(metres / 10) * 10} m`;
    const km = metres / 1000;
    return km < 100 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
  }
  const miles = metres / METRES_PER_MILE;
  // Under a tenth of a mile reads better in feet.
  if (miles < 0.1) return `${Math.round((metres * FEET_PER_METRE) / 10) * 10} ft`;
  return miles < 100 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
}

export function speedText(mps: number, units: Units = "metric"): string {
  return units === "metric" ? `${Math.round(mps * 3.6)} km/h` : `${Math.round((mps * 3600) / METRES_PER_MILE)} mph`;
}
